import React, { useEffect, useState } from "react";
import { Col, FormGroup, Input, Label, Row } from "reactstrap";
import { PROMOTION_STATUS, PROMOTION_MODES, DEFAULT_CITIES } from "../../../constants/promotionConstants";
import { GetCities } from "../../../requests";

const StepBasicInfo = ({ promotion, onChange, stores }) => {
  const [cities, setCities] = useState(DEFAULT_CITIES);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      try {
        const resp = await GetCities();
        const data = resp.data || [];
        const formatted = data.map((item) => ({
          value: item.id || item._id || item.value || item.name,
          label: item.name || item.label || item.value,
        }));
        if (mounted && formatted.length) setCities(formatted);
      } catch (err) {
        console.error("GetCities failed", err);
      }
    };
    fetch();
    return () => (mounted = false);
  }, []);

  return (
    <div>
      <h5 className="mb-4">Basic Information</h5>

      {/* Name + Code */}
      <Row className="mb-4">
        <Col md={6}>
          <FormGroup>
            <Label for="name">Promotion Name</Label>
            <Input
              id="name"
              value={promotion.name}
              onChange={(e) => onChange("name", e.target.value)}
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="code">Promotion Code (Optional)</Label>
            <Input
              id="code"
              value={promotion.code}
              onChange={(e) => onChange("code", e.target.value)}
              placeholder="FREEDEL"
            />
          </FormGroup>
        </Col>
      </Row>

      {/* Description */}
      <Row className="mb-3">
        <Col md={12}>
          <FormGroup>
            <Label for="description">Description</Label>
            <Input
              type="textarea"
              id="description"
              value={promotion.description}
              onChange={(e) => onChange("description", e.target.value)}
              rows={3}
            />
          </FormGroup>
        </Col>
      </Row>

      {/* Status / Mode / Priority */}
      <Row className="mb-3">
        <Col md={4}>
          <FormGroup>
            <Label for="promotionStatus">Promotion Status</Label>
            <Input
              type="select"
              id="promotionStatus"
              value={promotion.status}
              onChange={(e) => onChange("status", e.target.value)}
            >
              {PROMOTION_STATUS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label for="mode">Promotion Mode</Label>
            <Input
              type="select"
              id="mode"
              value={promotion.mode}
              onChange={(e) => onChange("mode", e.target.value)}
            >
              {PROMOTION_MODES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label for="priority">Priority</Label>
            <Input
              id="priority"
              type="number"
              min="1"
              value={promotion.priority}
              onChange={(e) => onChange("priority", e.target.value)}
            />
          </FormGroup>
        </Col>
      </Row>

      {/* Display meta — controls appearance in the mobile app */}
      <Row className="mb-3">
        <Col md={6}>
          <FormGroup>
            <Label for="icon">
              Icon <small className="text-muted">(emoji shown in coupon list, e.g. 🍔)</small>
            </Label>
            <Input
              id="icon"
              value={promotion.icon || ""}
              onChange={(e) => onChange("icon", e.target.value)}
              placeholder="🍔"
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="badge">
              Badge Label{" "}
              <small className="text-muted">(short tag on card, e.g. 🔥 Most Popular)</small>
            </Label>
            <Input
              id="badge"
              value={promotion.badge || ""}
              onChange={(e) => onChange("badge", e.target.value)}
              placeholder="🔥 Most Popular"
            />
          </FormGroup>
        </Col>
      </Row>

      {/* Store + Cities */}
      <Row className="mb-3">
        <Col md={4}>
          <FormGroup>
            <Label for="storeIds">Store</Label>
            <Input
              type="select"
              id="storeIds"
              multiple
              value={promotion.storeIds}
              onChange={(e) =>
                onChange(
                  "storeIds",
                  Array.from(e.target.selectedOptions, (option) => option.value)
                )
              }
            >
              {stores.map((store) => (
                <option
                  key={store.id || store.value || store.label}
                  value={store.id || store.username || store.value}
                >
                  {store.name || store.username || store.label || store.id}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label for="applicableCities">Applicable Cities</Label>
            <Input
              type="select"
              id="applicableCities"
              multiple
              value={promotion.applicableCities}
              onChange={(e) =>
                onChange(
                  "applicableCities",
                  Array.from(e.target.selectedOptions, (option) => option.value)
                )
              }
            >
              {cities.map((city) => (
                <option key={city.value} value={city.value}>
                  {city.label}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
      </Row>

      {/* Dates */}
      <Row className="mb-3">
        <Col md={4}>
          <FormGroup>
            <Label for="startDate">Start Date</Label>
            <Input
              type="date"
              id="startDate"
              value={promotion.startDate}
              onChange={(e) => onChange("startDate", e.target.value)}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label for="endDate">End Date</Label>
            <Input
              type="date"
              id="endDate"
              value={promotion.endDate}
              onChange={(e) => onChange("endDate", e.target.value)}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label for="expiryDate">
              Expiry Date{" "}
              <small className="text-muted">(shown to customers in the app)</small>
            </Label>
            <Input
              type="date"
              id="expiryDate"
              value={promotion.expiryDate || ""}
              onChange={(e) => onChange("expiryDate", e.target.value)}
            />
          </FormGroup>
        </Col>
      </Row>
    </div>
  );
};

export default StepBasicInfo;
